// An exam assignment's review (RFC-055 phase 2, byj-1wv.56.17.2): its
// settings and reviewers, what is reviewed and why, each student's total
// against the pass mark, what stops its grades being finished and the
// button that finishes them; a grade's reviews and the form a reviewer adds
// one with; and the assignment's audit trail.
import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type ExamReview, type GradeEvent, type ReviewState } from "../api";
import { signalName } from "../exams";
import { FormDialog } from "./FormDialog";
import { t, whenMinute, word } from "../i18n";
import { useQuestionName, useStaff } from "../staff";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** Why a refusal says the grades cannot be finished: the relay's problems, else its message. */
const problemsOf = (err: unknown): string[] => {
  const b = err instanceof ApiError ? (err.body as { problems?: string[] } | undefined) : undefined;
  return b?.problems ?? [errText(err)];
};

export type ReviewApi = Pick<typeof api, "examReview" | "setExamReview" | "addExamReviewer" | "removeExamReviewer" | "finalizeExam" | "reopenExam">;

/** The review tab: settings, reviewers, totals, what is reviewed, the final checks. */
export function ReviewPanel({ id, io = api, onChanged, questions = [] }: {
  id: string;
  io?: ReviewApi;
  onChanged?: () => void;
  /** the paper's questions by id, in order: a console names them by number, as its table does */
  questions?: readonly string[];
}) {
  const staff = useStaff();
  const questionName = useQuestionName(questions);
  const [st, setSt] = useState<ReviewState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  // the settings as the dialog edits them, taken from what they are as it opens
  const [draft, setDraft] = useState({ review: false, pass: "", sample: "", gap: "" });
  const numbers = [draft.pass, draft.sample, draft.gap].every((v) => v.trim() !== "" && Number.isFinite(Number(v)));
  const load = useCallback(() => {
    io.examReview(id).then(setSt, (err: unknown) => setError(errText(err)));
  }, [id, io]);
  useEffect(load, [load]);
  const act = async (f: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    setProblems([]);
    try {
      await f();
      load();
      onChanged?.();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setProblems(problemsOf(err));
      else setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  if (error && !st) return <p className="error">{t("common:error", { m: error })}</p>;
  if (!st) return <p className="muted">{t("common:loading")}</p>;
  const s = st.settings;
  const instructor = st.role === "instructor";
  const finished = !!s.finalized_at;
  const listed = !finished && !st.checks.ok ? st.checks.problems : [];
  const refusedToo = problems.filter((p) => !listed.includes(p));
  return (
    <div className="stack" data-testid="exam-review">
      <p className="muted flush">{t("exams:reviewPurpose")}</p>

      {/* ① how grades are reviewed, a line; changed and reviewers added in a dialog each */}
      <section className="review-step stack tight" data-testid="review-release">
        <div className="review-step-head">
          <h3 className="flush">{t("exams:stepSettings")}</h3>
          {instructor && !finished && (
            <button
              type="button"
              className="small"
              aria-controls="review-settings"
              onClick={() => {
                setDraft({ review: s.review, pass: String(s.pass_mark), sample: String(s.sample), gap: String(s.review_gap) });
                setEditing(true);
              }}
            >
              {t("exams:reviewEdit")}
            </button>
          )}
        </div>
        <p className="flush">
          <span className={`chip${s.review ? " chip-live" : ""}`}>{s.review ? t("exams:reviewOnShort") : t("exams:reviewOffShort")}</span>{" "}
          <span>{t("exams:reviewSettingsLine", { pass: s.pass_mark, sample: s.sample, gap: s.review_gap })}</span>
        </p>
        <div className="review-reviewers" data-testid="exam-reviewers">
          <span className="muted small">{t("exams:reviewers")}</span>
          {st.reviewers.length === 0 ? (
            <span className="muted small">{staff.noReviewers()}</span>
          ) : (
            st.reviewers.map((r) => (
              <span key={r.id} className="chip">
                {r.name || r.email}
                {instructor && (
                  <button
                    type="button"
                    className="chip-x"
                    aria-label={t("exams:reviewerRemoveNamed", { who: r.name || r.email })}
                    disabled={busy}
                    onClick={() => void act(() => io.removeExamReviewer(id, r.id))}
                  >
                    ×
                  </button>
                )}
              </span>
            ))
          )}
          {instructor && (
            <button type="button" className="small" aria-controls="reviewer-add" onClick={() => setAdding(true)}>
              {t("exams:reviewerAdd")}
            </button>
          )}
        </div>
        {editing && (
          <FormDialog
            id="review-settings"
            testid="review-settings"
            keepOnOutside
            title={t("exams:releaseTitle")}
            submitLabel={t("exams:reviewSave")}
            canSubmit={numbers}
            onSubmit={async () => {
              await io.setExamReview(id, { review: draft.review, pass_mark: Number(draft.pass), sample: Number(draft.sample), review_gap: Number(draft.gap) });
              setEditing(false);
              load();
              onChanged?.();
            }}
            onClose={() => setEditing(false)}
          >
            <div className="stack tight">
              <label className="toggle">
                <input type="checkbox" checked={draft.review} onChange={(e) => setDraft({ ...draft, review: e.target.checked })} />
                {t("exams:reviewToggle")}
              </label>
              <p className="hint">{t("exams:reviewToggleHint")}</p>
            </div>
            <label>
              {t("exams:passMark")}
              <input aria-label={t("exams:passMark")} inputMode="decimal" value={draft.pass} onChange={(e) => setDraft({ ...draft, pass: e.target.value })} />
              <span className="hint">{t("exams:passMarkHint")}</span>
            </label>
            <label>
              {t("exams:reviewSample")}
              <input aria-label={t("exams:reviewSample")} inputMode="numeric" value={draft.sample} onChange={(e) => setDraft({ ...draft, sample: e.target.value })} />
              <span className="hint">{t("exams:reviewSampleHint")}</span>
            </label>
            <label>
              {t("exams:reviewGap")}
              <input aria-label={t("exams:reviewGap")} inputMode="decimal" value={draft.gap} onChange={(e) => setDraft({ ...draft, gap: e.target.value })} />
              <span className="hint">{t("exams:reviewGapHint")}</span>
            </label>
          </FormDialog>
        )}
        {adding && (
          <FormDialog
            id="reviewer-add"
            testid="reviewer-add"
            keepOnOutside
            title={t("exams:reviewerAdd")}
            now={t("exams:reviewersLead")}
            submitLabel={t("exams:reviewerAddSubmit")}
            canSubmit={!!email.trim()}
            onSubmit={async () => {
              await io.addExamReviewer(id, email.trim());
              setEmail("");
              setAdding(false);
              load();
              onChanged?.();
            }}
            onClose={() => setAdding(false)}
          >
            <label>
              {t("exams:reviewerEmail")}
              <input aria-label={t("exams:reviewerEmail")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <span className="hint">{staff.reviewerHint()}</span>
            </label>
          </FormDialog>
        )}
      </section>

      {/* ② what is reviewed, and why each */}
      <section className="review-step stack tight" data-testid="review-step-items">
        <h3 className="flush">{t("exams:stepReview")}</h3>
        <p className="muted small flush">{s.review ? t("exams:stepReviewLead") : t("exams:reviewOff")}</p>
        <h4 className="flush">{t("exams:reviewItems")}</h4>
        <div className="doc-table exam-table" tabIndex={0}>
          <table data-testid="review-items">
            <thead>
              <tr>
                <th>{staff.person()}</th>
                <th>{t("exams:colQuestion")}</th>
                <th>{t("exams:colScore")}</th>
                <th>{t("exams:reviewWhyCol")}</th>
                <th>{t("exams:colState")}</th>
              </tr>
            </thead>
            <tbody>
              {st.items.map((it) => (
                <tr key={it.submission_id}>
                  <td>{it.student.name || it.student.email}</td>
                  <td>{staff.questionIds ? <code>{it.question}</code> : <span title={it.question}>{questionName(it.question)}</span>}</td>
                  <td>
                    {it.final != null ? it.final.toFixed(1) : "–"}
                    {it.score != null && it.final != null && it.final !== it.score && <span className="muted small"> ({it.score.toFixed(1)})</span>}
                  </td>
                  <td className="small">{it.why.map((w) => word("exams:reviewWhy", w) ?? w).join(", ") || "–"}</td>
                  <td>
                    <span className={`chip ${it.state === "to review" || it.state === "reviewers differ" ? "chip-warn" : it.state === "reviewed" || it.state === "decided" ? "chip-live" : ""}`}>
                      {word("exams:reviewState", it.state) ?? it.state}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ③ each one's total, what stops the grades being finished, and finishing them */}
      <section className="review-step stack tight" data-testid="review-finish">
        <h3 className="flush">{t("exams:stepFinish")}</h3>
        <p className="muted small flush">{t("exams:stepFinishLead")}</p>
        <h4 className="flush">{staff.totals()}</h4>
        <div className="doc-table exam-table" tabIndex={0}>
          <table data-testid="review-totals">
            <thead>
              <tr>
                <th>{staff.person()}</th>
                <th>{t("exams:colTotalScore")}</th>
                <th>{t("exams:colResult")}</th>
              </tr>
            </thead>
            <tbody>
              {st.totals.map((x) => (
                <tr key={x.student.id}>
                  <td>{x.student.name || x.student.email}</td>
                  <td>
                    <strong>{x.total.toFixed(1)}</strong>
                  </td>
                  <td>
                    <span className={`chip ${x.pass ? "chip-live" : "chip-warn"}`}>{x.pass ? t("exams:reviewPass") : t("exams:reviewBelow")}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card pad stack" data-testid="review-checks">
          <h4 className="flush">{finished ? t("exams:finalized", { when: whenMinute(s.finalized_at!) }) : t("exams:reviewChecks")}</h4>
          {!finished &&
            (st.checks.ok ? (
              <p className="small flush">{t("exams:reviewChecksOk")}</p>
            ) : (
              // a refusal naming what is listed here marks the list, not a copy of it
              <ul className={`small flush${problems.length > 0 ? " error" : ""}`}>
                {st.checks.problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            ))}
          {refusedToo.length > 0 && (
            <ul className="small flush error" data-testid="finalize-refused">
              {refusedToo.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
          {error && <p className="error">{t("common:error", { m: error })}</p>}
          {instructor &&
            (finished ? (
              <form
                className="row wrap"
                onSubmit={(e) => {
                  e.preventDefault();
                  const reason = String(new FormData(e.currentTarget).get("reason") ?? "").trim();
                  if (reason) void act(() => io.reopenExam(id, reason));
                }}
              >
                <label className="grow">
                  {t("exams:reopenReason")} <input name="reason" required />
                </label>
                <button type="submit" disabled={busy}>
                  {t("exams:reopen")}
                </button>
              </form>
            ) : (
              <div className="row">
                <button className="primary" disabled={busy} onClick={() => void act(() => io.finalizeExam(id))}>
                  {t("exams:finalize")}
                </button>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
}

/** A grade's reviews, and the form the instructor or a reviewer adds one with. */
export function GradeReviews({ gradeId, score, reviews, locked, onReviewed, signals = [], review = api.reviewGrade }: {
  gradeId: string;
  score: number;
  reviews: ExamReview[];
  locked: boolean;
  onReviewed: (r: ExamReview) => void;
  /** the fluency signals the judge decided on this grade, for the reviewer to label met or not (RFC-055's agreement) */
  signals?: string[];
  review?: typeof api.reviewGrade;
}) {
  const staff = useStaff();
  const [value, setValue] = useState(String(score));
  const [labels, setLabels] = useState<Record<string, boolean>>({});
  const [reason, setReason] = useState("confirmed");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="stack" data-testid="grade-reviews">
      <h3 className="flush">{t("exams:reviewsTitle")}</h3>
      <p className="muted small flush">{t("exams:reviewsLead")}</p>
      <p className="hint flush">{t("exams:reviewBasis", { score: score.toFixed(1) })}</p>
      {reviews.length === 0 ? (
        <p className="muted small flush">{t("exams:noReviews")}</p>
      ) : (
        <ul className="small flush">
          {reviews.map((r) => (
            <li key={r.id}>
              <strong>{r.score.toFixed(1)}</strong> — {r.reviewer.name || r.reviewer.email} · {word("exams:reviewReason", r.reason) ?? r.reason}
              {r.note && <span className="muted"> — {r.note}</span>}
            </li>
          ))}
        </ul>
      )}
      {locked ? (
        <p className="muted small flush">{staff.gradedHere ? t("exams:reviewLocked") : t("console:reviewLocked")}</p>
      ) : (
        <form
          className="stack card pad"
          onSubmit={(e) => {
            e.preventDefault();
            const n = Number(value);
            if (Number.isNaN(n) || n < 0 || n > 100) {
              setError(t("exams:reviewScoreRange"));
              return;
            }
            setBusy(true);
            setError(null);
            review(gradeId, { score: n, reason, note: note.trim(), ...(Object.keys(labels).length > 0 ? { parts: { labels } } : {}) }).then(
              (r) => {
                onReviewed(r);
                setNote("");
                setBusy(false);
              },
              (err: unknown) => {
                // a refusal of the grade's state (finished, or replaced by a regrade) said in the page's words
                setError(err instanceof ApiError && err.status === 409 ? t("exams:reviewRefused") : t("common:error", { m: errText(err) }));
                setBusy(false);
              },
            );
          }}
        >
          <label>
            {t("exams:reviewReasonLabel")}
            <select
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                // confirmed is the grade as it is: its score, whatever was typed
                if (e.target.value === "confirmed") setValue(String(score));
                setError(null);
              }}
            >
              {["confirmed", "evidence", "rubric", "judge", "integrity", "other"].map((r) => (
                <option key={r} value={r}>
                  {word("exams:reviewReason", r) ?? r}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t("exams:reviewScore")}
            <input aria-label={t("exams:reviewScore")} inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} readOnly={reason === "confirmed"} />
            <span className="hint">
              {reason === "confirmed" ? t("exams:reviewConfirmedHint", { score: score.toFixed(1) }) : t("exams:reviewChangeHint")}
            </span>
          </label>
          {reason !== "confirmed" && (
            <label>
              {t("exams:reviewNote")}
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} required />
            </label>
          )}
          {signals.length > 0 && (
            <fieldset className="stack" data-testid="review-labels">
              <legend className="small muted">{t("exams:reviewLabels")}</legend>
              {signals.map((s) => (
                <label key={s} className="row wrap small">
                  <span className="grow" title={s}>
                    {signalName(s)}
                  </span>
                  <select
                    aria-label={signalName(s)}
                    value={labels[s] === undefined ? "" : labels[s] ? "met" : "unmet"}
                    onChange={(e) =>
                      setLabels((m) => {
                        const { [s]: _, ...rest } = m;
                        return e.target.value === "" ? rest : { ...rest, [s]: e.target.value === "met" };
                      })
                    }
                  >
                    <option value="">{t("exams:labelNone")}</option>
                    <option value="met">{t("exams:labelMet")}</option>
                    <option value="unmet">{t("exams:labelUnmet")}</option>
                  </select>
                </label>
              ))}
            </fieldset>
          )}
          {error && <p className="error">{error}</p>}
          <div className="row">
            <button type="submit" disabled={busy}>
              {t("exams:reviewSend")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/** The assignment's audit trail, oldest first: a table as the hand-ins are, each change on whose hand-in where the page knows it. */
export function AuditPanel({ id, load = api.examAudit, whose }: {
  id: string;
  load?: typeof api.examAudit;
  /** whose hand-in and which question a change was on, from the hand-ins the page holds */
  whose?: (submission?: string) => string | undefined;
}) {
  const [events, setEvents] = useState<GradeEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    load(id).then(setEvents, (err: unknown) => setError(errText(err)));
  }, [id, load]);
  if (error) return <p className="error">{t("common:error", { m: error })}</p>;
  if (!events) return <p className="muted">{t("common:loading")}</p>;
  if (events.length === 0) return <p className="muted small">{t("exams:auditEmpty")}</p>;
  const result = (e: GradeEvent) =>
    e.after && "override" in e.after && e.after.override != null ? String(e.after.override) : e.after && "score" in e.after ? String(e.after.score) : "–";
  return (
    <div className="doc-table exam-table" tabIndex={0}>
      <table data-testid="exam-audit">
        <thead>
          <tr>
            <th>{t("exams:auditWhen")}</th>
            <th>{t("exams:auditWho")}</th>
            <th>{t("exams:auditWhat")}</th>
            <th>{t("exams:auditOn")}</th>
            <th>{t("exams:auditResult")}</th>
            <th>{t("exams:auditNote")}</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id}>
              <td className="nowrap">{whenMinute(e.created_at)}</td>
              <td>{e.actor.name || e.actor.email}</td>
              <td>
                <strong>{word("exams:auditAction", e.action) ?? e.action}</strong>
              </td>
              <td>{whose?.(e.submission_id) ?? "–"}</td>
              <td>{result(e)}</td>
              <td className="muted">{e.note || "–"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
